import { createContext, useState, useMemo, SetStateAction } from 'react';
import { Dialog } from '@headlessui/react'
import { ZipIcon } from '../Icons';
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/20/solid'
import { displayUnits } from 'src/components/Dependencies'
import { useLifeCycleStages, resolveLifeCycle } from "src/lib/lifecycle"
import { mkConfig, generateCsv } from "export-to-csv";
import { useMetadataTabularizer } from 'src/lib/metadata'
import Button from 'src/components/Button'
import ExcelJS, { Fill, Borders } from 'exceljs'

import { AppContext, useAppContext } from 'src/lib/appContext';
import { HierarchyController } from '../PartHierarchy';
import { shortDateTime } from 'src/lib/formatters';
import { getInstanceQuantity } from './aggregation';
import snakeCase from 'lodash.snakecase';

export type Rows = Record<string, any>[]
export type ExportFormat = 'standardized' | 'classic'

export type SelectedExportInfo = ExportInfo & {
  selectedOption: Rows
}
type ExportDialogProps = {
  openUsingRows: SelectedExportInfo | undefined
  setOpenUsingRows: (open: SelectedExportInfo | undefined) => void
} & ExportBodyProps

type ExportBodyProps = {
  hierarchyController: HierarchyController
}

type ExportConfigProps = {
  rows: Rows
  fileName: string
  onClose: () => void
}
export type ExportMapper = AppContext['currentOrg']['manualExportMapper']
export const ExportMapperContext = createContext<ExportMapper>(null)

export const ExportDialog: React.FC<ExportDialogProps> = ({ openUsingRows, setOpenUsingRows }) => {
  if (!openUsingRows) {
    return null;
  }

  return (
    <Dialog
      open={Boolean(openUsingRows)}
      onClose={() => setOpenUsingRows(undefined)}
      className="relative z-10"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-8 py-6 text-left shadow-xl min-w-[28rem]">
            <div>
              <div className="">
                <Dialog.Title className="font-medium mb-5 text-lg text-gray-900">
                  Export
                </Dialog.Title>
                {openUsingRows && (
                  <ExportConfig
                    rows={openUsingRows.selectedOption}
                    fileName={openUsingRows.name}
                    onClose={() => setOpenUsingRows(undefined)}
                  />
                )}
              </div>
            </div>

          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}

const ExportConfig = ({ rows, fileName, onClose }: ExportConfigProps) => {
  const [fileType, setFileType] = useState<'csv' | 'xlsx'>('csv');

  const handleDownload = () => {
    if (fileType === 'csv') {
      // Generate CSV and trigger download
      const csv = generateCsv(csvConfig)(rows);
      const csvContent = `data:text/csv;charset=utf-8,${csv}`;
      const encodedUri = encodeURI(csvContent).replaceAll('#', '%23');
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `${fileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Download XLSX
      downloadXlsx(rows, `${fileName}.xlsx`);
    }
  };

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col gap-3'>
        <div className="text-xs text-gray-500 -mt-2 mb-1">
          Choose the file format for your export.
        </div>
        <div className="flex gap-3">
          <button
            className={`px-4 py-2 rounded-md border ${fileType === 'csv' ? 'bg-blue-50 border-blue-300' : 'border-gray-300'}`}
            onClick={() => setFileType('csv')}
          >
            CSV
          </button>
          <button
            className={`px-4 py-2 rounded-md border ${fileType === 'xlsx' ? 'bg-blue-50 border-blue-300' : 'border-gray-300'}`}
            onClick={() => setFileType('xlsx')}
          >
            Excel (XLSX)
          </button>
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        <div className="text-sm text-gray-500 font-medium">
          File Preview
        </div>
        <div className="flex items-center gap-4 my-2 border border-gray-200 rounded-lg px-5 py-5 -mx-1">
          {fileType === 'csv' ? (
            <DocumentTextIcon className='h-10 w-10 text-green-500' />
          ) : (
            <DocumentTextIcon className='h-10 w-10 text-blue-500' />
          )}
          <div className='flex-1 text-left'>{`${fileName}.${fileType}`}</div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-3">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant='primary' onClick={handleDownload} data-testid="download-bom-export">
          Download
        </Button>
      </div>
    </div>
  )
}

type CsvLinkProps = {
  fileName: string
  testId?: string
  body: Record<string, unknown>[]
}
export const csvConfig = mkConfig({ useKeysAsHeaders: true })
export const CsvLink: React.FC<CsvLinkProps> = ({ body, fileName, testId }) => {
  const csv = generateCsv(csvConfig)(body)
  console.log({body, csv})
  const csvContent = `data:text/csv;charset=utf-8,${csv}`;

  const encodedUri = encodeURI(csvContent).replaceAll('#', '%23');
  return <a
    data-testId={testId || 'csv-download-link'}
    className='flex items-center gap-4 my-2 border border-gray-200 rounded-lg px-5 py-5 hover:bg-gray-50 -mx-1'
    href={encodedUri} download={fileName}>
    <DocumentTextIcon className='h-10 w-10 text-green-500' />
    <div className='flex-1'>{fileName}</div>
    <ArrowDownTrayIcon className='h-5 w-5 text-gray-500' />
  </a>
}

export const PlainCsvLink: React.FC<CsvLinkProps & { children: React.ReactNode }> = ({ body, fileName, children, testId }) => {
  // generateCsv throws error when body is empty array
  const csv = body.length === 0 ? '' : generateCsv(csvConfig)(body)
  const csvContent = `data:text/csv;charset=utf-8,${csv}`;

  const encodedUri = encodeURI(csvContent).replaceAll('#', '%23');
  return <a
    data-testId={testId || 'csv-download-link'}
    href={encodedUri} download={fileName}>
    <div className='flex-1'>{children}</div>
  </a>
}


type ArtifactDownload = {
  source?: string
  sourceContent?: string
  destination: string
}

type ArtifactsBundleProps = {
  filename: string
  files: ArtifactDownload[]
  align?: 'left' | 'center'
}

export const ArtifactsBundle: React.FC<ArtifactsBundleProps> = ({ filename, files, align }) => {
  return <button
    className='flex items-center gap-4 my-2 border border-gray-200 rounded-lg px-5 py-5 hover:bg-gray-50 -mx-1'
    onClick={() => generateDownloadBundle({ files, filename })}>
    <ZipIcon className='h-10 w-10 text-gray-500' />
    <div className={`flex-1 ${align === 'left' ? 'text-left' : ''}`}>{filename}</div>
    <ArrowDownTrayIcon className='h-5 w-5 text-gray-500' />
  </button>
}

type GenerateBundleInput = {
  filename: string
  files: ArtifactDownload[]
}
export const generateDownloadBundle = async ({ files, filename }: GenerateBundleInput) => {
  const jszip = new JSZip()
  const filesWithContent = await Promise.all(files.map(async file => {
    if (file.sourceContent) return file
    const response = await fetch(file.source!, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-cache'
    });
    return {
      destination: file.destination,
      sourceContent: await response.blob()
    }
  }))
  for (let file of filesWithContent) {
    jszip.file(file.destination, file.sourceContent!);
  }
  jszip.generateAsync({ type: "blob" }).then(function (content) {
    saveAs(content, filename);
  });
}

// Helper function to download XLSX files
export const downloadXlsx = async (rows: Rows, fileName: string) => {
  console.log({rows})
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Export');

  // If there are no rows, return an empty workbook
  if (rows.length === 0) {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
    return;
  }

  // Add headers
  const headers = Object.keys(rows[0]!);
  worksheet.columns = headers.map(header => ({
    header,
    key: header,
    width: Math.max(15, header.length * 1.2) // Dynamic width based on header length
  }));

  // Add styling to headers
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  };
  headerRow.border = {
    bottom: { style: 'thin' }
  };

  // Add data rows
  rows.forEach(row => {
    const rowValues = headers.map(header => row[header]);
    worksheet.addRow(rowValues);
  });

  // Auto-filter
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length }
  };

  // Generate and save the file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, fileName);
};

export const calculateExports = ({ hierarchyController, format = 'classic' }: ExportBodyProps & { format?: ExportFormat }) => {
  const tabularizeMetadata = useMetadataTabularizer()
  const appContext = useAppContext()
  const exportMapper = appContext.currentOrg.manualExportMapper
  const stages = useLifeCycleStages()

  if (hierarchyController.roots.length === 0) {
    return {
      name: '',
      csv: [],
      flatCsv: [],
      bundle: []
    }
  }

  const fullTree = hierarchyController.roots.flatMap((root, i) => {
    const filteredTree = root.tree.filter(n => !n.filterRemove)
    const selectedByLevel = Object.fromEntries(root.selectedNodes.map(key => [key, true]))
    const selectedHierarchy = hierarchyController.selectable ?
      filteredTree.filter(n => selectedByLevel[n.hierarchy]) : filteredTree
    return selectedHierarchy.map(node => {
      const [top, ...levels] = node.hierarchy.split('.')

      const base = `${i+1}`
      const children = levels.length ? `.${levels.join('.')}` : ''

      return {
        ...node,
        hierarchy: base + children
      }
    })
  })

  const downloads = fullTree
    .reduce((deduped, h, i) => {
      const exists = deduped.some(e => {
        return h.part.partNumber === e.part.partNumber &&
          h.part.version === e.part.version
      })
      if (!exists) {
        return [...deduped, h]
      }
      return deduped
    }, [] as typeof fullTree)
    .flatMap(h => {
      return h.part.artifacts.map(a => {
        return {
          source: a.file.url,
          destination: `${h.part.partNumber}v${h.part.version} - ${h.part.name}/${a.filename}`
        }
      })
    })

  const sourcesHeaderPrefixes =
    Array(fullTree.reduce((count, p) => Math.max(count, p.part.sources.length), 0)).fill(null)
      .map((_, i) => `Source ${i + 1} `)

  type Sources = (typeof fullTree)[number]['part']['sources']
  const tabularizeSources = (sources: Sources) =>
    sourcesHeaderPrefixes.reduce((acc, prefix, idx) => {
      const source = sources[idx];
      return {
        ...acc,
        [prefix + 'Distributor']: source?.distributor?.name ?? '',
        [prefix + 'SKU']: source?.distributorSku ?? '',
        [prefix + 'Link']: source?.url ?? '',
        [prefix + 'Comment']: source?.comment ?? '',
        [prefix + 'Price']: source?.price ?? '',
        [prefix + 'Currency']: source?.priceCurrency ?? '',
        [prefix + 'Lead Time Days']: source?.leadTimeDays ?? '',
        [prefix + 'Per Quantity']: source?.perQuantity ?? '',
        [prefix + 'Per Quantity Unit']: source?.perQuantityUnit ?? '',
        [prefix + 'Stock']: source?.stock ?? '',
      }
    }, {} as Record<string, any>)

  const hierarchyCsvBody = fullTree.map(h => {
    const part = {
      ['Part Number']: h.part.partNumber,
      ['Version']: h.part.version,
      ['CAD Revision']: h.part.cadRev ?? undefined,
      ['Category ID']: h.part.proto.category.id,
      ['Category Name']: h.part.proto.category.name,
      ['Is Option']: h.part.nodeType === 'PartGroup' ? 'TRUE' : undefined,
      ['Name']: h.part.name,
      ['Lifecycle']: stages?.length ? resolveLifeCycle(stages, h.part.lifeCycle).name : undefined,
      ['Summary']: h.part.summary ?? undefined,
      ['Quantity']: h.parentToNodeDependency?.quantity ?? undefined,
      ['Quantity Units']: displayUnits(h.parentToNodeDependency?.units),
    }
    const extras = {
      ...tabularizeSources(h.part.sources),
      ...tabularizeMetadata(h.part.metadata),
    }
    if (hierarchyController.hierarchyMode === 'flat') return { ...part, ...extras }
    return {
      ['Hierarchy']: h.hierarchy,
      ...part,
      ['Reference Designator']: h.parentToNodeDependency?.referenceDesignator ?? '',
      ...extras
    }
  })


  // flat CSV is only designed to work in part cell
  // where there is only one BOM to be flattened
  const firstRoot = hierarchyController.roots[0]!
  const selectedWithRoot = ['1', ...firstRoot.selectedNodes]
  const selectedByLevel = Object.fromEntries(selectedWithRoot.map(key => [key, true]))

  const filteredTree = firstRoot.tree.filter(n => !n.filterRemove && n.part.nodeType === 'Part')
  const remainingTree = hierarchyController.selectable ? filteredTree.filter(n => selectedByLevel[n.hierarchy]) : filteredTree

  const flatCsvBody = remainingTree.reduce((rolledUp, h) => {
    const matchingIndex = rolledUp.findIndex(e => {
      return h.part.partNumber === e.part.partNumber &&
        h.part.version === e.part.version
    })
    if (matchingIndex === -1) {
      return [...rolledUp, {
        ...h,
      }]
    }
    return rolledUp.map((e, i) => {
      if (matchingIndex !== i) {
        return e
      }

      return {
        ...e,
        dependency: {
          ...e.parentToNodeDependency,
          quantity: (e.parentToNodeDependency?.quantity || 0) + (h.parentToNodeDependency?.quantity || 0),
          units: displayUnits(h.parentToNodeDependency?.units),
          designator: e.parentToNodeDependency?.referenceDesignator
        },
      }
    })
  }, [] as ((typeof remainingTree)[number] & { quantityRange?: [number, number] })[]).map(h => {
    const { quantity: q, units: error } = getInstanceQuantity(firstRoot.hierarchyTree, h.part.partNumber, h.part.version)
    return {
      ['Part Number']: h.part.partNumber,
      ['Version']: h.part.version,
      ['Category ID']: h.part.proto.category.id,
      ['Category Name']: h.part.proto.category.name,
      ['Name']: h.part.name,
      ['Lifecycle']: stages?.length ? resolveLifeCycle(stages, h.part.lifeCycle).name : undefined,
      ['Summary']: h.part.summary,
      ['Quantity']: (q && q[1]) ?
        (q[0] === q[1] ? q[0] : `${q[0]}-${q[1]}`)
        : '',
      ['Quantity Units']: displayUnits(h.parentToNodeDependency?.units),
      ['Reference Designator']: h.parentToNodeDependency?.referenceDesignator ?? '',
      ...tabularizeSources(h.part.sources),
      ...tabularizeMetadata(h.part.metadata),
    }
  })

  type R = Record<string, any>
  const withMappedKeys = (objs: Object[]) => exportMapper ?
    objs.map(obj =>
      Object.entries(obj).reduce((mapped, [k, v]) => ({
        ...mapped,
        [(exportMapper as R)[k] ?? k]: v
      }), {} as R))
    :
    objs as R[];

  const mappedHierarchy = withMappedKeys(hierarchyCsvBody)

  const makeName = () => {
    if (hierarchyController.roots.length === 1) {
      const part = hierarchyController.roots[0]!.rootNode.part
      return `${part.partNumber}-v${part.version}`
    }
    return `Export ${shortDateTime(new Date())}`
  }
  const name = makeName()

  const files = () => {
    if (Object.keys(mappedHierarchy).length === 0) return downloads
    const csvConfig = mkConfig({ useKeysAsHeaders: true })
    const hierarchyCsvContents = String(generateCsv(csvConfig)(mappedHierarchy))
    const filesWithCsvs = [...downloads, {
      sourceContent: hierarchyCsvContents,
      destination: `${name} - Hierarchical Bill of Materials.csv`
    }]
    return filesWithCsvs
  }

  return {
    name,
    csv: mappedHierarchy,
    flatCsv: flatCsvBody,
    bundle: files()
  }
}
type ExportInfo = ReturnType<typeof calculateExports>

